import React from 'react';
import { Badge, Button } from '@syneto/compass-react';
import Preloader from '../preloader';

/*
 * What the fleet is actually running for the chosen component type, next to the fields that name
 * versions. Clicking a version selects it, which is the whole point: the operator should be
 * picking from what is out there rather than recalling version strings.
 *
 * Counts are stated in two units on purpose. "96 components" explains a number that looks
 * implausible for a drive; "14 appliances" is the one a decision's blast radius is measured in.
 *
 * Shared by advisory authoring and component type approval, which select from the same list for
 * opposite reasons - one names a version to warn about, the other one to endorse (spec D18). The
 * list is identical; only the sentences around it differ, so the two callers supply their own copy
 * rather than this component guessing which feature is rendering it.
 */
const FleetFirmwareVersions = (props) => {
	const {
		versions,
		isLoading,
		error,
		isPartial,
		scannedCount,
		matchedCount,
		totalItems,
		unreportedCount,
		retry,
		onSelectVersion,
		selectedVersions,
		manualEntryHint,
		unreportedNote
	} = props;

	if (isLoading) {
		return (
			<div className="mb-3">
				<Preloader dataIsLoading={true} />
			</div>
		);
	}

	/*
	 * Not escalated to the page-level alert: the field below still works as free text, so this is
	 * a degraded helper rather than a broken page.
	 */
	if (error) {
		return (
			<p className="text-danger mb-3">
				{error} You can still type versions manually.
				<Button role="tertiary" onClick={retry}>Retry</Button>
			</p>
		);
	}

	/*
	 * `manufacturer` and `model` are partial matches server-side, so the endpoint can return rows
	 * for a neighbouring type and none for this one. Saying that beats an unexplained empty list.
	 */
	if (versions.length === 0) {
		return (
			<p className="text-muted mb-3">
				{matchedCount === 0 && totalItems > 0
					? `No components of this exact type were found in the scanned range. ${manualEntryHint}`
					: `No firmware versions reported for this component type. ${manualEntryHint}`}
			</p>
		);
	}

	const countLabel = (option) => {
		const prefix = isPartial ? 'at least ' : '';

		return `${prefix}${option.componentCount} component${option.componentCount === 1 ? '' : 's'} `
			+ `on ${option.applianceCount} appliance${option.applianceCount === 1 ? '' : 's'}`;
	};

	return (
		<div className="mb-3">
			<p className="mb-1">Seen in the fleet</p>

			<div className="d-flex flex-wrap gap-2">
				{versions.map((option) => {
					return (
						<Button
							key={option.version}
							role="tertiary"
							disabled={(selectedVersions ?? []).includes(option.version)}
							onClick={() => { return onSelectVersion(option.version); }}
						>
							{option.version}

							{option.seenInScan
								? <span className="text-muted ms-2">{countLabel(option)}</span>
								: <span className="text-muted ms-2">not seen in the scanned pages</span>}

							{/*
								Taken from the API's own analytics row and compared only for equality - the
								version-aware ordering behind them is not something the UI can reproduce.
							*/}
							{option.isOldest && <Badge appearance="secondary" className="ms-2">oldest</Badge>}
							{option.isNewest && <Badge appearance="secondary" className="ms-2">newest</Badge>}
							{option.isApproved && <Badge appearance="info" className="ms-2">approved</Badge>}
						</Button>
					);
				})}
			</div>

			{/*
				A capped scan undercounts, and presenting a sample as a complete enumeration is how a
				version that is really out there ends up unconsidered.
			*/}
			{isPartial && (
				<p className="text-muted mb-0 mt-1">
					Scanned the first {scannedCount} of {totalItems} components for this type. Other firmware
					versions may exist in the fleet. {manualEntryHint}
				</p>
			)}

			{unreportedCount > 0 && unreportedNote && (
				<p className="text-muted mb-0 mt-1">{unreportedNote}</p>
			)}
		</div>
	);
};

export default FleetFirmwareVersions;
